// src/context/PopupContext.js

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getSuperAdminCompanyParams } from '../utils/superAdminCompany';

const PopupContext = createContext();

const formatMySQLDateTime = (date) => {
  const pad = (value) => String(value).padStart(2, '0');

  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate())
  ].join('-') + ' ' + [
    pad(date.getHours()),
    pad(date.getMinutes()),
    pad(date.getSeconds())
  ].join(':');
};

export const PopupProvider = ({ children }) => {
  const [popupMessages, setPopupMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [newLeadsCount, setNewLeadsCount] = useState(0);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const leadsBaselineKey = 'leadsNotificationBaselineTime';
  const leadsBaselineRef = useRef(sessionStorage.getItem(leadsBaselineKey));

  const addPopupMessage = (popup) => {
    setPopupMessages(prevMessages => {
      // Check if this specific message has already been shown
      const existingMessageIndex = prevMessages.findIndex(
        msg => msg.customer?.phone_no_primary === popup.customer?.phone_no_primary
      );

      if (existingMessageIndex !== -1) {
        // Update existing message with new priority/time
        const updatedMessages = [...prevMessages];
        updatedMessages[existingMessageIndex] = popup;
        return updatedMessages;
      } else {
        // Add new message
        return [...prevMessages, popup];
      }
    });
  };

  const removePopupMessage = (index) => {
    setPopupMessages(prevMessages => prevMessages.filter((_, i) => i !== index));
  };

  const clearNotificationPopups = () => {
    setPopupMessages(prevMessages => prevMessages.filter(m => m.type !== 'notification'));
  };

  const acknowledgeNewLeads = useCallback(() => {
    const now = formatMySQLDateTime(new Date());
    leadsBaselineRef.current = now;
    sessionStorage.setItem(leadsBaselineKey, now);
    setNewLeadsCount(0);
  }, []);

  const getReminders = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem('token');

      if (!token) return;

      const response = await axios.get(`${apiUrl}/customers/reminders`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: getSuperAdminCompanyParams()
      });

      // Process reminders for popup notifications
      response.data.forEach(reminder => {
        const minutesUntil = Math.floor((new Date(reminder.scheduled_at) - new Date()) / (1000 * 60));

        // Only show popup for reminders within 15 minutes
        if (minutesUntil <= 15) {
          let priority;
          let color;

          if (minutesUntil <= 5) {
            priority = 'high';
            color = 'red';
          } else if (minutesUntil <= 15) {
            priority = 'medium';
            color = 'orange';
          }

          const popup = {
            customer: reminder,
            priority,
            color,
            minutesUntil,
            onClick: () => {
              window.location.href = `/customers/phone/${reminder.phone_no_primary}`;
            }
          };

          addPopupMessage(popup);
        }
      });
    } catch (error) {
      console.error('Error fetching reminders:', error);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for notifications and pending delete approval requests
  const fetchNotificationsAndApprovals = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem('token');
      if (!token) return;

      // 1. Fetch pending approvals (for popups)
      const approvalsRes = await axios.get(`${apiUrl}/customer-delete-requests/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const approvals = approvalsRes.data?.data || [];
      setPendingApprovals(approvals);

      // 2. Fetch notifications (for badge and popups)
      const notificationsRes = await axios.get(`${apiUrl}/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const notifications = notificationsRes.data?.data || [];
      setUnreadCount(notificationsRes.data?.unreadCount || 0);

      // Delete approval requests and task alerts are shown in /notifications.
      // Only delete outcomes are shown as floating popups.
      notifications.forEach(notif => {
        const shouldPopup = (
          (notif.type === 'delete_approved' || notif.type === 'delete_rejected')
          && !notif.is_read
        );

        if (shouldPopup) {
          setPopupMessages(prev => {
            const alreadyShown = prev.some(m => m.notificationId === notif.id);
            if (alreadyShown) return prev;
            return [
              ...prev,
              {
                type: 'notification',
                notificationId: notif.id,
                title: notif.title,
                message: notif.message,
                notifType: notif.type,
                refId: notif.ref_id,
                createdAt: notif.created_at
              }
            ];
          });
        }
      });
    } catch (error) {
      if (error.response?.status !== 403 && error.response?.status !== 404) {
        console.error('Error fetching notifications/approvals:', error);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchNewLeadsCount = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!apiUrl || !token) return;

      let baselineTime = leadsBaselineRef.current || sessionStorage.getItem(leadsBaselineKey);
      if (!baselineTime) {
        baselineTime = formatMySQLDateTime(new Date());
        leadsBaselineRef.current = baselineTime;
        sessionStorage.setItem(leadsBaselineKey, baselineTime);
      }

      const companyParams = getSuperAdminCompanyParams();
      const response = await axios.get(`${apiUrl}/leads/new-count`, {
        params: { since: baselineTime, ...companyParams },
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data?.success) {
        if (leadsBaselineRef.current !== baselineTime) return;
        setNewLeadsCount(response.data.newLeadsCount || 0);
      }
    } catch (error) {
      if (error.response?.status !== 403 && error.response?.status !== 404) {
        console.error('Error checking new leads:', error);
      }
    }
  }, []);

  useEffect(() => {
    // Initial fetches
    getReminders();
    fetchNotificationsAndApprovals();
    fetchNewLeadsCount();

    // Poll every 60 seconds
    const remindersInterval = setInterval(getReminders, 60000);
    const notificationsInterval = setInterval(fetchNotificationsAndApprovals, 30000); // Poll notifications more frequently
    const newLeadsInterval = setInterval(fetchNewLeadsCount, 10000);

    return () => {
      clearInterval(remindersInterval);
      clearInterval(notificationsInterval);
      clearInterval(newLeadsInterval);
    };
  }, [getReminders, fetchNotificationsAndApprovals, fetchNewLeadsCount]);

  return (
    <PopupContext.Provider value={{
      popupMessages,
      addPopupMessage,
      removePopupMessage,
      clearNotificationPopups,
      unreadCount,
      newLeadsCount,
      hasNewLeads: newLeadsCount > 0,
      acknowledgeNewLeads,
      pendingApprovals,
      refreshApprovals: fetchNotificationsAndApprovals
    }}>
      {children}
    </PopupContext.Provider>
  );
};

export const usePopup = () => {
  const context = useContext(PopupContext);
  if (!context) {
    throw new Error('usePopup must be used within a PopupProvider');
  }
  return context;
};
